import { HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http'

import {
  capitalize as _capitalize,
  isArray as _isArray,
  mergeWith as _mergeWith,
  split as _split,
} from 'lodash/fp'

import { Observable, first, from, fromEvent, merge } from 'rxjs'
import { map } from 'rxjs/operators'

import { SelectItem } from '@flowx/angular-ui-toolkit'

export type OptionalExcept<T, TRequired extends keyof T> = Partial<T> & Required<Pick<T, TRequired>>

export type JsonValue =
  string | number | boolean | null | undefined | Date | { [key: string]: JsonValue } | JsonValue[]

export const mergeWithArrayOverwrite = _mergeWith((value, srcValue) => {
  if (_isArray(value) || _isArray(srcValue)) {
    return srcValue
  }
})

export const isMacOS = navigator.userAgent.toLowerCase().includes('mac')

export const cancelBrowserShortcut = (event) => event.preventDefault()

export function isSelectItem<T>(item: T | SelectItem<T>): item is SelectItem<T> {
  return (item as SelectItem<T>)?.value !== undefined
}

export function getMimeTypeFromFileName(filename: string): string {
  const fileExtension = filename.split('.').pop()?.toLowerCase()
  switch (fileExtension) {
    case 'pdf':
      return 'application/pdf'
    case 'doc':
      return 'application/msword'
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    case 'txt':
      return 'text/plain'
    case 'csv':
      return 'text/plain'
    case 'xls':
      return 'application/vnd.ms-excel'
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    case 'png':
      return 'image/png'
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg'
    case 'mp3':
      return 'audio/mpeg'
    case 'wav':
      return 'audio/wav'
    case 'm4a':
      return 'audio/x-m4a'
    case 'aac':
      return 'audio/aac'
    case 'ogg':
      return 'audio/ogg'
    default:
      return 'application/octet-stream'
  }
}

/**
 * Whether a file can be rendered inline by the browser (so it makes sense to
 * open it in a new tab). Binary Office formats (xls/xlsx/docx) cannot, and
 * should be downloaded instead of opening an empty tab.
 */
export function isInlinePreviewableFile(filename: string): boolean {
  const mimeType = getMimeTypeFromFileName(filename)
  return (
    mimeType === 'application/pdf' ||
    mimeType.startsWith('image/') ||
    mimeType.startsWith('audio/') ||
    mimeType.startsWith('video/') ||
    mimeType.startsWith('text/')
  )
}

export const isZip = (file: File): boolean =>
  file.type === 'application/zip' ||
  file.type === 'application/x-zip-compressed' ||
  file.name.toLowerCase().endsWith('.zip')

export function fileNameFromPath(path: string, separator: string = '/'): string {
  return path?.split(separator).pop() ?? ''
}

/**
 * Display a blob in an already-open window by creating a typed object URL
 * and navigating to it. Handles media-aware URL revocation.
 */
export function displayBlobInWindow(targetWindow: Window, blob: Blob, filename: string): void {
  const mimeType = getMimeTypeFromFileName(filename)
  const properBlob = new Blob([blob], { type: mimeType })
  const objectUrl = URL.createObjectURL(properBlob)
  targetWindow.location.href = objectUrl

  // Audio/video files need the object URL to remain valid while playing.
  // Revoke it when the opened tab is closed instead of using a short timeout.
  const isMediaType = mimeType.startsWith('audio/') || mimeType.startsWith('video/')
  if (isMediaType) {
    const checkClosed = setInterval(() => {
      if (targetWindow.closed) {
        clearInterval(checkClosed)
        URL.revokeObjectURL(objectUrl)
      }
    }, 1000)
  } else {
    setTimeout(() => {
      URL.revokeObjectURL(objectUrl)
    }, 3000)
  }
}

export function openBlobInNewTab(blob: Blob, filename: string): boolean {
  if (!(blob instanceof Blob)) {
    console.warn('Not a valid Blob!')
    return
  }

  const newWindow = window.open('about:blank', '_blank')
  if (!newWindow) return false
  displayBlobInWindow(newWindow, blob, filename)
  return !!newWindow
}

export function downloadBlob(blob: Blob, filename: string): void {
  if (!(blob instanceof Blob)) {
    console.warn('Not a valid Blob!')
    return
  }

  const blobUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = filename
  link.dispatchEvent(
    new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    })
  )
}

const safeDecodeURIComponent = (value: string): string | null => {
  try {
    return decodeURIComponent(value)
  } catch {
    return null
  }
}

/**
 * Reads the filename from a Content-Disposition header; RFC 5987 `filename*` wins over plain
 * `filename`. Path separators are stripped so a header-supplied name can't escape the download
 * folder. Returns null when absent, malformed, or blank, so callers can `??` a fallback.
 */
export const fileNameFromContentDisposition = (header: string | null): string | null => {
  const encoded = /filename\*=(?:[\w-]+'[\w-]*')?([^;]+)/i.exec(header ?? '')?.[1]
  const plain = /filename="?([^";]+)"?/i.exec(header ?? '')?.[1]
  const name = (encoded ? safeDecodeURIComponent(encoded) : null) ?? plain
  return name?.replace(/[/\\]/g, '_').trim() || null
}

/**
 * Downloads an `HttpResponse<Blob>` under the server-provided Content-Disposition filename,
 * falling back to `fallbackFileName` when the header is missing or unreadable.
 */
export function downloadHttpResponse(response: HttpResponse<Blob>, fallbackFileName: string): void {
  if (!response.body) {
    return
  }
  downloadBlob(
    response.body,
    fileNameFromContentDisposition(response.headers.get('content-disposition')) ?? fallbackFileName
  )
}

export function loadLocalFile({ accept }: { accept: string[] }): Observable<FileList> {
  const fileInput = document.createElement('input')
  fileInput.accept = accept.join(', ')
  fileInput.type = 'file'
  fileInput.dispatchEvent(
    new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    })
  )

  return fromEvent(fileInput, 'change').pipe(
    first(),
    map((ev) => (ev.target as HTMLInputElement).files)
  )
}

export function loadLocalFileWithCancel({ accept }: { accept: string[] }): Observable<FileList> {
  const fileInput = document.createElement('input')
  fileInput.accept = accept.join(', ')
  fileInput.type = 'file'

  fileInput.dispatchEvent(
    new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    })
  )

  return merge(fromEvent(fileInput, 'change'), fromEvent(fileInput, 'cancel')).pipe(
    map((event) => {
      if (event.type === 'cancel') {
        return null
      }

      return (event.target as HTMLInputElement).files
    })
  )
}

export function jsonToBlob(json: Record<any, any>): Blob {
  const stringEncoded = JSON.stringify(json)
  return new Blob([stringEncoded], { type: 'application/json' })
}

export function csvToBlob(csv: string) {
  return new Blob([csv], { type: 'application/csv' })
}

export function parseFileAsJson(file: File): Observable<Record<string, any>> {
  return from(file.text()).pipe(map((text) => JSON.parse(text)))
}

export function toBoolean(value: string | boolean): boolean {
  return value === true || value === 'true'
}

export function enumKeyToUILabel(key: string): string {
  const tokens = _split('_', key)
  const capitalized = tokens.map(_capitalize)
  return capitalized.join(' ')
}

export function enumToSelectOptions(
  enumValue: Record<string, any>
): { label: string; value: any }[] {
  return Object.keys(enumValue).map((key) => {
    return {
      label: enumKeyToUILabel(key),
      value: enumValue[key],
    }
  })
}

export function enumValuesArrayToSelectOptions(
  enumValues: string[]
): { label: string; value: string }[] {
  return enumValues.map((value) => {
    return {
      label: enumKeyToUILabel(value),
      value,
    }
  })
}

export const httpStatusOf = (err: unknown): number | undefined =>
  (err as { status?: number })?.status
export const errorKeyOf = (err: unknown): string | undefined =>
  (err as { error?: { errorKey?: string } })?.error?.errorKey

export const errorTitle = (err: unknown, fallback: string): string =>
  (err as { error?: { title?: string } })?.error?.title ?? fallback

export function isBlobError(err: HttpErrorResponse): boolean {
  return err instanceof HttpErrorResponse && err.error instanceof Blob
}

export function parseErrorBlob(err: HttpErrorResponse): Observable<any> {
  const reader: FileReader = new FileReader()
  const obs = new Observable((observer: any) => {
    reader.onloadend = () => {
      observer.error(
        new HttpErrorResponse({
          ...err,
          error: {
            defaultMessage: reader.result,
          },
        })
      )
      observer.complete()
    }
  })
  reader.readAsText(err.error)
  return obs
}

export function indexFound(index): boolean {
  return index > -1
}

export function insertAtIndex<T>(array: T[], index: number, newItems: T[]): T[] {
  return [...array.slice(0, index), ...newItems, ...array.slice(index)]
}

export function parseJwt<T>(token: string): T {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  )

  return JSON.parse(jsonPayload) as T
}

export function svgElementFromString(str): SVGElement {
  const div = document.createElement('DIV')
  div.innerHTML = str
  const svg = div.querySelector('svg')

  if (!svg) {
    throw Error('<svg> tag not found')
  }

  return svg
}

export function deepOmit(object, paths: string[]): any {
  return paths.reduce((acc, path) => removePropertiesAtPath(acc, path), object)
}

function removePropertiesAtPath(obj, path): any {
  const parts = path.split('.')
  const currentKey = parts.shift()
  if (_isArray(obj) && currentKey === '*') {
    return obj.map((item) => removePropertiesAtPath(item, parts.join('.')))
  }

  if (isWildcardPath(currentKey)) {
    const wildcardIndex = currentKey.indexOf('*')
    const wildcardPrefix = currentKey.slice(0, wildcardIndex)
    const wildcardSuffix = currentKey.slice(wildcardIndex + 1)

    const filteredKeys = Object.keys(obj).filter(
      (key) => key.startsWith(wildcardPrefix) && key.endsWith(wildcardSuffix)
    )

    const updatedObj = filteredKeys.reduce((acc, key) => {
      if (parts.length === 0) {
        return { ...acc, [key]: undefined }
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        const updatedValue = removePropertiesAtPath(obj[key], parts.join('.'))

        return { ...acc, [key]: updatedValue }
      }
      return acc
    }, {})

    return { ...obj, ...updatedObj }
  } else {
    if (parts.length === 0) {
      if (_isArray(obj)) {
        return obj.map((item) => {
          const { [currentKey]: deleted, ...rest } = item
          return rest
        })
      }
      const { [currentKey]: deleted, ...rest } = obj
      return rest
    } else if (typeof obj[currentKey] === 'object' && obj[currentKey] !== null) {
      const updatedValue = removePropertiesAtPath(obj[currentKey], parts.join('.'))
      return { ...obj, [currentKey]: updatedValue }
    }
    return obj
  }
}

export function isWildcardPath(path): boolean {
  return path.includes('*')
}

export function jwtUserToProfile(user: {
  family_name: string
  given_name: string
  sub: string
  email: string
  realm_access: {
    roles: string[]
  }
}): { name: string; initials: string; id: string; email: string; hasDesignerRole: boolean } {
  const names = []
  const initials = []
  if (user.given_name) {
    names.push(user.given_name)
    initials.push(user.given_name.charAt(0))
  }
  if (user.family_name) {
    names.push(user.family_name)
    initials.push(user.family_name.charAt(0))
  }
  if (!user.given_name && !user.family_name && user.email) {
    names.push(user.email)
    initials.push(user.email.charAt(0))
  }

  return {
    name: names?.join(' '),
    initials: initials?.join(''),
    id: user.sub,
    email: user.email,
    hasDesignerRole: user.realm_access?.roles?.includes('DESIGNER_USER'),
  }
}

/**
 * @method
 * @description return httpParams object
 * @params {any} params
 * @returns HttpParams returns httpParams
 */
export function addParams(params: Record<any, any>): HttpParams {
  let httpParams = new HttpParams()

  Object.keys(params).forEach((key) => {
    if (key && params[key] !== undefined && params[key] !== null) {
      if (Array.isArray(params[key])) {
        const paramsString = params[key].join(',')
        httpParams = httpParams.append(key, paramsString)
      } else {
        httpParams = httpParams.append(key, params[key])
      }
    }
  })

  return httpParams
}

export function isHttpSuccessStatusCode(code: number) {
  return !!code && code >= 200 && code < 300
}

export function isHttpConflictStatusCode(code: number) {
  return !!code && code === 409
}

export function hashCode256(str: string): string {
  if (typeof str !== 'string') {
    return '0'.repeat(64) // 64 hex chars = 256 bits
  }

  // Generate 8 separate 32-bit hashes using different string variations
  const seeds = ['XyZ123', 'AbC456', 'LmN789', 'QrS012', 'TuV345', 'WxY678', 'ZaB901', 'CdE234']
  const hashes: string[] = []

  for (let i = 0; i < 8; i++) {
    // Create variation of input string for each hash
    const variation = `${seeds[i]}${str}${i}`
    let hash = 0
    for (let i = 0; i < variation.length; i++) {
      const char = variation.charCodeAt(i)
      // eslint-disable-next-line no-bitwise
      hash = (hash << 5) - hash + char
      // eslint-disable-next-line no-bitwise
      hash = hash & hash // convert to 32bit integer
    }
    // eslint-disable-next-line no-bitwise
    hash = hash >>> 0 // convert signed to unsigned https://stackoverflow.com/a/1908655

    const hexHash = Number(hash).toString(16).padStart(8, '0')
    hashes.push(hexHash)
  }

  return hashes.join('').toLowerCase()
}
